import { computed, ComputedRef } from 'vue'

/**
 * Combines the error messages from apollo and vulidate into a single compute prop where apollo validation errors take
 * precedence over vulidate errors. It then expose them as ref props on an object.
 *
 * Example:
 *
 * const mutationVariables = ref({
 *   myProp: 'my bad input >:)'
 * })
 *
 * const apollo = useApolloErrors(mutationError)
 * const vulidate = useCustomVuelidate(rules, mutationVariables, { $autoDirty: true })
 * const { myProp } = useApolloAndVuelidate(apollo, vulidate)
 *
 * console.log(myProp) // Should contain vulidate error
 *
 * await submitMutation()
 *
 * console.log(myProp) // Should contain apollo validation error
 *
 * @param apollo {object} output from useApolloError composable
 * @param vulidate {object} output from useCustomVuelidate composable
 * @param apolloErrorPrefix {string} apollo errors can have a prefix attach due to using input as the base variable.
 *                                   This is taken into consideration here. Only add the base variable name and not
 *                                   a dot as well. Ie input if my apollo object looks like { input: { myProp } }
 * @return object
 */
export default function useApolloAndVuelidate(
  apollo: { validationErrors: any },
  vulidate: any,
  apolloErrorPrefix = '',
): Record<string, ComputedRef<string>> {
  const { validationErrors } = apollo

  const keys = Object.keys(vulidate).filter(
    (key) =>
      !['v$', 'touchAll', 'resetAll', 'anyDirty', 'quietErrors', 'error'].includes(key) &&
      !key.endsWith('Touch') &&
      !key.endsWith('Reset'),
  )

  return keys.reduce(
    (prev, key) => ({
      [key]: computed(() => {
        const apolloKey = apolloErrorPrefix ? `${apolloErrorPrefix}.${key}` : key
        return validationErrors.value[apolloKey] ?? vulidate[key]?.value
      }),
      ...prev,
    }),
    {},
  )
}
